import { api } from "@repo/convex/api";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { useConvexAuth, useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { getWorkOSConvexTokenStatusSnapshot } from "@/hooks/use-auth-from-workos";
import { useObservedAction } from "@/hooks/use-observed-convex";
import { breadcrumb, captureError } from "@/lib/observability";
import { resolveAuthRedirectDestination, toAppPath } from "./-redirect-storage";

/**
 * WorkOS echoes back the `state` we handed to `signIn()` (see `login.tsx`).
 * TanStack Router auto-parses a JSON search param into an object, but the raw
 * string still shows up depending on how the round trip is encoded, so both
 * shapes are accepted; a bare `returnTo` param is the last resort.
 */
function extractReturnTo(search: Record<string, unknown>): unknown {
  const { state } = search;
  if (state && typeof state === "object") {
    return (state as Record<string, unknown>).returnTo;
  }
  if (typeof state === "string") {
    try {
      return (JSON.parse(state) as Record<string, unknown> | null)?.returnTo;
    } catch {
      return undefined;
    }
  }
  return search.returnTo;
}

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
  // `toAppPath` keeps the parsed destination a same-origin app path, exactly
  // like `/auth/login`'s `redirect` param.
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: toAppPath(extractReturnTo(search)),
  }),
});

/**
 * Timeout for Convex authentication validation.
 * If Convex doesn't validate the JWT within this time, something is likely misconfigured.
 * 15 seconds is long enough for slow networks but short enough to be helpful.
 */
const AUTH_VALIDATION_TIMEOUT_MS = 15_000;

/**
 * OAuth callback handler for WorkOS AuthKit.
 *
 * This page handles the redirect back from WorkOS after authentication.
 * It waits for BOTH AuthKit AND Convex to confirm authentication before
 * navigating to the protected route.
 *
 * Why we wait for convexAuthenticated (not just user):
 * - AuthKit user means: browser has a WorkOS token
 * - convexAuthenticated means: Convex backend has VALIDATED the JWT
 *
 * Since our router uses useConvexAuth() for route protection, we must wait
 * for Convex validation to complete. Otherwise, navigating to a protected
 * route would immediately redirect back to login (because Convex isn't ready).
 */
function AuthCallback() {
  const { isLoading: authKitLoading, user } = useAuth();
  const { isAuthenticated: convexAuthenticated, isLoading: convexLoading } = useConvexAuth();
  const navigate = useNavigate();
  const { returnTo } = Route.useSearch();

  // Capture returnTo early - WorkOS AuthKit clears URL params after handling
  // OAuth (history.replaceState), so the search this route was mounted with can
  // go empty under us. The ref latches the first real destination we see.
  const returnToRef = useRef<string>("/");
  const resolvedReturnTo = resolveAuthRedirectDestination(returnTo);
  if (returnToRef.current === "/" && resolvedReturnTo !== "/") {
    returnToRef.current = resolvedReturnTo;
  }

  // Just-in-time user provisioning
  // Note: ensureUser is now an ACTION (not mutation) to support SDK fallback
  const ensureUser = useObservedAction(api.users.ensureUser, {
    feature: "auth",
    operation: "ensure-user",
    includeRequestId: true,
  });
  const [provisioningState, setProvisioningState] = useState<"idle" | "provisioning" | "error">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const provisioningStarted = useRef(false);

  // Timeout detection for silent JWT validation failures
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const timeoutStartRef = useRef<number>(Date.now());

  // Full page load, not SPA navigation: a failed auth flow can leave AuthKit's
  // and the Convex client's in-memory state stuck, and only a reload resets
  // them. An in-app navigate() here produced retry buttons that did nothing.
  const restartSignIn = () => {
    window.location.assign(`/auth/login?redirect=${encodeURIComponent(returnToRef.current)}`);
  };

  // Backend auth diagnostics - helps identify configuration issues
  // This query is unauthenticated and safe to call always
  const authDiagnostics = useQuery(api.diagnostics.authHealth, {});

  // If the local WorkOS -> Convex token bridge already knows token acquisition
  // failed, recover through login instead of reporting it as Convex JWT failure.
  useEffect(() => {
    if (!user || convexAuthenticated || convexLoading || hasTimedOut) {
      return;
    }

    const tokenStatus = getWorkOSConvexTokenStatusSnapshot();
    if (tokenStatus.status !== "unavailable") {
      return;
    }

    breadcrumb(
      "auth.callback.token-unavailable",
      {
        tokenStatus: tokenStatus.status,
        reason: tokenStatus.reason ?? "unknown",
        redirectTo: "/auth/login",
      },
      { category: "navigation", level: "warning" }
    );

    navigate({ to: "/auth/login", search: { redirect: returnToRef.current } });
  }, [user, convexAuthenticated, convexLoading, hasTimedOut, navigate]);

  // Timeout detection: if user has WorkOS session but Convex never validates,
  // something is likely misconfigured (e.g., missing JWT template in WorkOS)
  useEffect(() => {
    // Only start timeout if we have a user but Convex hasn't authenticated
    if (!user || convexAuthenticated || hasTimedOut) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const elapsedMs = Date.now() - timeoutStartRef.current;
      const tokenStatus = getWorkOSConvexTokenStatusSnapshot();

      if (tokenStatus.status === "unavailable") {
        breadcrumb(
          "auth.validation.token-unavailable-timeout",
          {
            elapsedMs,
            reason: tokenStatus.reason ?? "unknown",
            hasWorkOSUser: true,
            convexAuthenticated: false,
            convexLoading,
          },
          { category: "user", level: "warning" }
        );
        navigate({ to: "/auth/login", search: { redirect: returnToRef.current } });
        return;
      }

      // Add breadcrumb for debugging context
      breadcrumb(
        "auth.validation.timeout",
        {
          elapsedMs,
          hasWorkOSUser: true,
          convexAuthenticated: false,
          convexLoading,
        },
        { category: "user", level: "warning" }
      );

      // Capture to Sentry with diagnostic context (no PII)
      // Include backend diagnostics if available for better debugging
      captureError(new Error("Auth validation timeout: Convex JWT validation failed"), {
        feature: "auth",
        operation: "callback",
        extra: {
          elapsedMs,
          hasWorkOSUser: true,
          convexAuthenticated: false,
          convexLoading,
          // Helps identify environment-specific issues
          environment: import.meta.env.MODE,
          // Backend diagnostics (safe - no secrets)
          backendClientIdConfigured: authDiagnostics?.clientIdConfigured,
          backendProviderCount: authDiagnostics?.providerCount,
          backendEnvironment: authDiagnostics?.environment,
        },
      });

      setHasTimedOut(true);
    }, AUTH_VALIDATION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [user, convexAuthenticated, convexLoading, hasTimedOut, authDiagnostics, navigate]);

  useEffect(() => {
    // Track auth state changes for debugging (breadcrumb appears in Sentry timeline)
    breadcrumb(
      "auth.callback.state",
      {
        authKitLoading,
        hasUser: Boolean(user),
        convexAuthenticated,
        convexLoading,
        provisioningState,
      },
      { category: "user" }
    );

    // If AuthKit finished loading but there's no user, the OAuth flow failed
    // (code expired, already used, network error, etc.) - redirect back to login
    if (!authKitLoading && !user) {
      breadcrumb(
        "auth.callback.noUser",
        { reason: "OAuth flow failed or code expired", redirectTo: "/auth/login" },
        { category: "navigation", level: "warning" }
      );
      // Use captured returnTo from ref
      navigate({ to: "/auth/login", search: { redirect: returnToRef.current } });
      return;
    }

    // Wait for Convex authentication before attempting user provisioning
    if (!convexAuthenticated || convexLoading) {
      return;
    }

    // Ensure user exists in database (just-in-time provisioning)
    // This handles preview environments where webhooks don't fire
    if (!provisioningStarted.current && provisioningState === "idle") {
      provisioningStarted.current = true;
      setProvisioningState("provisioning");

      breadcrumb("auth.provisioning.start", {}, { category: "user" });

      ensureUser({})
        .then(() => {
          // Use captured returnTo from ref (in case URL was cleared)
          const destination = returnToRef.current;
          breadcrumb(
            "auth.provisioning.success",
            { navigateTo: destination },
            { category: "navigation" }
          );
          // `href`, not `to`: the destination is a raw path string that may
          // carry a query and hash, and only `href` is run through the
          // router's `parseHref()` (`to` never splits `?`/`#`, leaving the
          // in-memory location without them).
          navigate({ href: destination });
        })
        .catch((error) => {
          const message = error instanceof Error ? error.message : "Failed to complete sign in";
          breadcrumb(
            "auth.provisioning.error",
            {
              message,
              tokenStatus: getWorkOSConvexTokenStatusSnapshot().status,
            },
            { category: "user", level: "error" }
          );

          // Capture provisioning failure to Sentry
          captureError(error, {
            feature: "auth",
            operation: "provisioning",
            extra: { returnTo },
          });

          setProvisioningState("error");
          setErrorMessage(message);
        });
    }
  }, [
    authKitLoading,
    user,
    convexAuthenticated,
    convexLoading,
    navigate,
    returnTo,
    ensureUser,
    provisioningState,
  ]);

  // The auth layout handles centering, so we just render the content

  // Timeout: WorkOS authenticated but Convex JWT validation never completed
  if (hasTimedOut) {
    return (
      <div className="text-center max-w-md mx-auto">
        <div className="text-amber-500 mb-4">
          <svg
            className="h-10 w-10 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            role="img"
            aria-label="Clock icon indicating timeout"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-foreground font-medium mb-2">Sign in is taking longer than expected</p>
        <p className="text-muted-foreground text-sm mb-4">
          Your account was verified, but we couldn't complete the sign-in process.
        </p>

        {/* Confidence builder: team has been notified */}
        <div className="bg-muted/50 rounded-lg p-3 mb-4 text-left">
          <p className="text-xs text-muted-foreground mb-2">
            <span className="text-green-600 font-medium">✓ Our team has been notified</span> and
            will investigate this issue.
          </p>

          {/* Show specific hint if we detect missing configuration */}
          {authDiagnostics && !authDiagnostics.clientIdConfigured && (
            <p className="text-xs text-amber-600 mb-2">⚠️ Backend configuration issue detected</p>
          )}

          <p className="text-xs text-muted-foreground font-medium mb-1">
            This usually happens when:
          </p>
          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
            <li>The authentication service is being configured</li>
            <li>There's a temporary connection issue</li>
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={restartSignIn}
            className="text-sm text-primary hover:underline font-medium"
          >
            Try signing in again
          </button>
          <p className="text-xs text-muted-foreground">
            If this keeps happening, please contact support.
          </p>
        </div>
      </div>
    );
  }

  if (provisioningState === "error") {
    return (
      <div className="text-center">
        <div className="text-destructive mb-4">
          <svg
            className="h-8 w-8 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            role="img"
            aria-label="Warning icon indicating sign-in failure"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="text-destructive font-medium mb-2">Sign in failed</p>
        <p className="text-muted-foreground text-sm mb-4">{errorMessage}</p>
        <button
          type="button"
          onClick={restartSignIn}
          className="text-sm text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="text-center">
      <Spinner className="size-8 text-primary mx-auto mb-4" />
      <p className="text-muted-foreground">Completing sign in...</p>
      {user && !convexAuthenticated && (
        <p className="text-xs text-amber-500 mt-2">Validating session...</p>
      )}
      {convexAuthenticated && provisioningState === "provisioning" && (
        <p className="text-xs text-amber-500 mt-2">Setting up your account...</p>
      )}
    </div>
  );
}
